import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PartyPopper, Package, Calendar, LogOut, Plus, Edit, Trash2, Check, X, Loader2, Eye, Download, AlertTriangle, TrendingUp, ChevronLeft, ChevronRight, Settings, Construction, Palmtree, Server, Mail, Key, Shield, Users, UserPlus, Crown, UserCheck, FileText, Menu, BarChart3, DollarSign, ShoppingBag, CalendarDays, ArrowUp, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert 24-hour time to 12-hour format
const formatTime12hr = (time) => {
  if (!time) return "";
  try {
    const [hour, minute] = time.split(':').map(Number);
    const period = hour < 12 ? "AM" : "PM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time;
  }
};

// Session timeout duration (1 hour in milliseconds)
const SESSION_TIMEOUT = 60 * 60 * 1000; // 60 minutes

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bookings");
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [savingBookingEdit, setSavingBookingEdit] = useState(false);
  const [invoiceAdjustDialog, setInvoiceAdjustDialog] = useState(false);
  const [invoiceAdjustForm, setInvoiceAdjustForm] = useState({ booking: null, discount_amount: 0, notes: "", resend_email: true });
  const [savingInvoiceAdjust, setSavingInvoiceAdjust] = useState(false);
  const [productForm, setProductForm] = useState({ name: "", name_es: "", description: "", description_es: "", category: "chairs", price_per_day: "", total_inventory: "", image_url: "", image_urls: [] });
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [cleanupMode, setCleanupMode] = useState("before"); // "before", "after", "between"
  const [cleanupDate, setCleanupDate] = useState("");
  const [cleanupStartDate, setCleanupStartDate] = useState("");
  const [cleanupEndDate, setCleanupEndDate] = useState("");
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarPopup, setCalendarPopup] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Session timeout effect - check every minute
  useEffect(() => {
    const checkSessionTimeout = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        // Session expired - logout and redirect
        localStorage.removeItem("adminAuth");
        localStorage.removeItem("adminUser");
        toast.error("Session expired due to inactivity. Please log in again.");
        navigate("/admin");
      }
    };

    // Check session every minute
    const interval = setInterval(checkSessionTimeout, 60000);

    // Activity event listeners
    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    // Track user activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      clearInterval(interval);
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [lastActivity, navigate]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("We're getting things ready. Please check back soon.");
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  // Vacation Mode state
  const [vacationMode, setVacationMode] = useState(false);
  const [vacationStartDate, setVacationStartDate] = useState("");
  const [vacationEndDate, setVacationEndDate] = useState("");
  const [savingVacation, setSavingVacation] = useState(false);
  // Social Media state
  const [instagramUrl, setInstagramUrl] = useState("https://www.instagram.com/magnolia.eventsrental");
  const [facebookUrl, setFacebookUrl] = useState("https://www.facebook.com/share/1AcmPpFtMc/?mibextid=wwXIfr");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [tiktokEnabled, setTiktokEnabled] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);
  // System Configuration state
  const [systemConfig, setSystemConfig] = useState({
    resend_api_key: "",
    sender_email: "",
    reply_to_email: "",
    admin_email: ""
  });
  const [savingSystem, setSavingSystem] = useState(false);
  // User Management state
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [savingUser, setSavingUser] = useState(false);
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);
  // Homepage Content state
  const [homepageContent, setHomepageContent] = useState({
    hero_image_url: "",
    hero_title_en: "",
    hero_title_es: "",
    hero_description_en: "",
    hero_description_es: "",
    delivery_pickup_fee: 20,
    equipment_categories: []
  });
  const [savingHomepage, setSavingHomepage] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: "",
    key: "",
    title_en: "",
    title_es: "",
    description_en: "",
    description_es: "",
    image_url: "",
    link: "/products"
  });
  // Detailed Analytics state
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRange, setAnalyticsRange] = useState("month"); // week, month, year, custom
  const [analyticsStartDate, setAnalyticsStartDate] = useState("");
  const [analyticsEndDate, setAnalyticsEndDate] = useState("");

  const adminAuth = localStorage.getItem("adminAuth");
  const storedUser = localStorage.getItem("adminUser");

  const fetchData = useCallback(async () => {
    setLoading(true);

    let user = null;
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        setCurrentUser(user);
      } catch (e) {
        setCurrentUser(null);
      }
    } else {
      setCurrentUser(null);
    }

    try {
      const requests = await Promise.allSettled([
        axios.get(`${API}/bookings`, { headers: { Authorization: adminAuth } }),
        axios.get(`${API}/products`),
        axios.get(`${API}/analytics`, { headers: { Authorization: adminAuth } }),
        axios.get(`${API}/settings/maintenance`),
        axios.get(`${API}/settings/vacation`),
        axios.get(`${API}/settings/social`),
        axios.get(`${API}/settings/system`, { headers: { Authorization: adminAuth } }),
        axios.get(`${API}/settings/homepage`),
      ]);

      const [bRes, pRes, aRes, mRes, vRes, sRes, sysRes, hRes] = requests;

      const firstAuthError = requests.find(
        (result) => result.status === "rejected" && result.reason?.response?.status === 401
      );
      if (firstAuthError) {
        localStorage.removeItem("adminAuth");
        localStorage.removeItem("adminUser");
        navigate("/admin");
        return;
      }

      if (bRes.status === "fulfilled") setBookings(bRes.value.data);
      if (pRes.status === "fulfilled") setProducts(pRes.value.data || []);
      if (aRes.status === "fulfilled") setAnalytics(aRes.value.data);
      if (mRes.status === "fulfilled") {
        setMaintenanceMode(mRes.value.data.enabled);
        setMaintenanceMessage(mRes.value.data.message || "We're getting things ready. Please check back soon.");
      }
      if (vRes.status === "fulfilled") {
        setVacationMode(vRes.value.data.enabled);
        setVacationStartDate(vRes.value.data.start_date || "");
        setVacationEndDate(vRes.value.data.end_date || "");
      }
      if (sRes.status === "fulfilled") {
        setInstagramUrl(sRes.value.data.instagram_url || "");
        setFacebookUrl(sRes.value.data.facebook_url || "");
        setTiktokUrl(sRes.value.data.tiktok_url || "");
        setTiktokEnabled(sRes.value.data.tiktok_enabled || false);
      }
      if (sysRes.status === "fulfilled") {
        setSystemConfig({
          resend_api_key: "", // Don't show the actual key, only masked version
          resend_api_key_masked: sysRes.value.data.resend_api_key_masked,
          resend_api_key_configured: sysRes.value.data.resend_api_key_configured,
          sender_email: sysRes.value.data.sender_email || "",
          reply_to_email: sysRes.value.data.reply_to_email || "",
          admin_email: sysRes.value.data.admin_email || "",
          booking_notification_emails: sysRes.value.data.booking_notification_emails || ""
        });
      }
      if (hRes.status === "fulfilled") {
        setHomepageContent({
          hero_image_url: hRes.value.data.hero_image_url || "",
          hero_title_en: hRes.value.data.hero_title_en || "Make Your Event Unforgettable",
          hero_title_es: hRes.value.data.hero_title_es || "Haz Tu Evento Inolvidable",
          hero_description_en: hRes.value.data.hero_description_en || "",
          hero_description_es: hRes.value.data.hero_description_es || "",
          delivery_pickup_fee: hRes.value.data.delivery_pickup_fee ?? 20,
          equipment_categories: hRes.value.data.equipment_categories || []
        });
      }

      if (pRes.status === "rejected") {
        console.error("Could not fetch products:", pRes.reason?.response?.data?.detail || pRes.reason?.message);
      }

      // Fetch users if manager
      if (user?.role === "manager") {
        try {
          const usersRes = await axios.get(`${API}/users`, { headers: { Authorization: adminAuth } });
          setUsers(usersRes.data);
        } catch (e) {
          // User might not have permission
          console.log("Could not fetch users:", e.response?.data?.detail);
        }
      }
    } catch (e) {
      console.error("Failed to load admin data:", e);
    } finally { setLoading(false); }
  }, [adminAuth, navigate, storedUser]);

  useEffect(() => {
    if (!adminAuth) { navigate("/admin"); return; }
    fetchData();
  }, [adminAuth, navigate, fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status`, { status }, { headers: { Authorization: adminAuth } });
      toast.success(`Booking ${status}!`);
      fetchData();
      setBookingDialog(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateBookingDetails = async (bookingId, payload) => {
    setSavingBookingEdit(true);
    try {
      const response = await axios.put(`${API}/bookings/${bookingId}`, payload, {
        headers: { Authorization: adminAuth }
      });
      toast.success("Booking updated successfully");
      setSelectedBooking(response.data);
      fetchData();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update booking details");
      return false;
    } finally {
      setSavingBookingEdit(false);
    }
  };

  const openProductForm = (p = null) => {
    setEditingProduct(p);
    setProductForm(p ? { 
      name: p.name, 
      name_es: p.name_es || "", 
      description: p.description, 
      description_es: p.description_es || "", 
      category: p.category, 
      price_per_day: String(p.price_per_day), 
      total_inventory: String(p.total_inventory), 
      image_url: p.image_url,
      image_urls: p.image_urls || []
    } : { name: "", name_es: "", description: "", description_es: "", category: "chairs", price_per_day: "", total_inventory: "", image_url: "", image_urls: [] });
    setProductDialog(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...productForm, 
        name_es: productForm.name_es || null,
        description_es: productForm.description_es || null,
        price_per_day: parseFloat(productForm.price_per_day), 
        total_inventory: parseInt(productForm.total_inventory),
        image_urls: productForm.image_urls.filter(url => url.trim() !== "")
      };
      if (editingProduct) await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers: { Authorization: adminAuth } });
      else await axios.post(`${API}/products`, payload, { headers: { Authorization: adminAuth } });
      toast.success(editingProduct ? "Updated!" : "Created!");
      setProductDialog(false);
      fetchData();
    } catch (e) { toast.error("Failed to save"); }
  };

  const toggleMaintenanceMode = async () => {
    setSavingMaintenance(true);
    try {
      const newState = !maintenanceMode;
      await axios.put(`${API}/settings/maintenance`, 
        { enabled: newState, message: maintenanceMessage },
        { headers: { Authorization: adminAuth } }
      );
      setMaintenanceMode(newState);
      toast.success(`Maintenance mode ${newState ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error("Failed to update maintenance mode");
    } finally {
      setSavingMaintenance(false);
    }
  };

  const toggleVacationMode = async (newState) => {
    // If turning on, validate dates first
    if (newState && (!vacationStartDate || !vacationEndDate)) {
      toast.error("Please select both start and end dates");
      return;
    }
    if (newState && vacationEndDate < vacationStartDate) {
      toast.error("End date must be after start date");
      return;
    }
    
    setSavingVacation(true);
    try {
      await axios.put(`${API}/settings/vacation`, 
        { 
          enabled: newState, 
          start_date: newState ? vacationStartDate : null,
          end_date: newState ? vacationEndDate : null
        },
        { headers: { Authorization: adminAuth } }
      );
      setVacationMode(newState);
      toast.success(`Vacation mode ${newState ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update vacation mode");
    } finally {
      setSavingVacation(false);
    }
  };

  const saveVacationDates = async () => {
    if (!vacationStartDate || !vacationEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    if (vacationEndDate < vacationStartDate) {
      toast.error("End date must be after start date");
      return;
    }
    
    setSavingVacation(true);
    try {
      await axios.put(`${API}/settings/vacation`, 
        { 
          enabled: vacationMode, 
          start_date: vacationStartDate,
          end_date: vacationEndDate
        },
        { headers: { Authorization: adminAuth } }
      );
      toast.success("Vacation dates updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update vacation dates");
    } finally {
      setSavingVacation(false);
    }
  };

  const saveSocialMedia = async () => {
    setSavingSocial(true);
    try {
      await axios.put(`${API}/settings/social`, 
        { 
          instagram_url: instagramUrl, 
          facebook_url: facebookUrl,
          tiktok_url: tiktokUrl,
          tiktok_enabled: tiktokEnabled
        },
        { headers: { Authorization: adminAuth } }
      );
      toast.success("Social media links updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update social links");
    } finally {
      setSavingSocial(false);
    }
  };

  const saveSystemConfig = async (field, value) => {
    setSavingSystem(true);
    try {
      const payload = { [field]: value };
      await axios.put(`${API}/settings/system`, payload, { headers: { Authorization: adminAuth } });
      toast.success(`${field.replace(/_/g, ' ')} updated successfully`);
      // Refresh the data to get updated masked key
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update system configuration");
    } finally {
      setSavingSystem(false);
    }
  };

  // User Management functions
  const openUserForm = (user = null) => {
    setEditingUser(user);
    setUserForm(user ? {
      name: user.name,
      email: user.email,
      password: "", // Don't show password when editing
      role: user.role
    } : { name: "", email: "", password: "", role: "viewer" });
    setUserDialog(true);
  };

  const saveUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      if (editingUser) {
        // Update user (only name and role)
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: userForm.name,
          role: userForm.role
        }, { headers: { Authorization: adminAuth } });
        toast.success("User updated successfully!");
      } else {
        // Create new user
        if (!userForm.password || userForm.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setSavingUser(false);
          return;
        }
        await axios.post(`${API}/users`, userForm, { headers: { Authorization: adminAuth } });
        toast.success("User created successfully!");
      }
      setUserDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save user");
    } finally {
      setSavingUser(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: { Authorization: adminAuth } });
      toast.success("User deleted successfully!");
      setDeleteUserDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  const toggleUserActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, {
        is_active: !user.is_active
      }, { headers: { Authorization: adminAuth } });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update user status");
    }
  };

  // Homepage Content Functions
  const saveHeroImage = async () => {
    setSavingHomepage(true);
    try {
      await axios.put(`${API}/settings/homepage`, {
        hero_image_url: homepageContent.hero_image_url
      }, { headers: { Authorization: adminAuth } });
      toast.success("Hero image updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSavingHomepage(false);
    }
  };

  const saveHeroText = async () => {
    setSavingHomepage(true);
    try {
      await axios.put(`${API}/settings/homepage`, {
        hero_title_en: homepageContent.hero_title_en,
        hero_title_es: homepageContent.hero_title_es,
        hero_description_en: homepageContent.hero_description_en,
        hero_description_es: homepageContent.hero_description_es
      }, { headers: { Authorization: adminAuth } });
      toast.success("Hero text updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSavingHomepage(false);
    }
  };


  const saveDeliveryFee = async () => {
    setSavingHomepage(true);
    try {
      await axios.put(`${API}/settings/homepage`, {
        delivery_pickup_fee: Number(homepageContent.delivery_pickup_fee)
      }, { headers: { Authorization: adminAuth } });
      toast.success("Delivery fee updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update delivery fee");
    } finally {
      setSavingHomepage(false);
    }
  };

  const openCategoryForm = (cat = null) => {
    setEditingCategory(cat);
    setCategoryForm(cat ? {
      id: cat.id,
      key: cat.key,
      title_en: cat.title_en,
      title_es: cat.title_es,
      description_en: cat.description_en,
      description_es: cat.description_es,
      image_url: cat.image_url,
      link: cat.link || "/products"
    } : {
      id: `cat-${Date.now()}`,
      key: "",
      title_en: "",
      title_es: "",
      description_en: "",
      description_es: "",
      image_url: "",
      link: "/products"
    });
    setCategoryDialog(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    setSavingHomepage(true);
    try {
      let updatedCategories;
      if (editingCategory) {
        updatedCategories = homepageContent.equipment_categories.map(cat =>
          cat.id === editingCategory.id ? categoryForm : cat
        );
      } else {
        updatedCategories = [...homepageContent.equipment_categories, categoryForm];
      }
      
      await axios.put(`${API}/settings/homepage`, {
        equipment_categories: updatedCategories
      }, { headers: { Authorization: adminAuth } });
      
      setHomepageContent(prev => ({ ...prev, equipment_categories: updatedCategories }));
      setCategoryDialog(false);
      toast.success(editingCategory ? "Category updated!" : "Category added!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save category");
    } finally {
      setSavingHomepage(false);
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm("Delete this category?")) return;
    setSavingHomepage(true);
    try {
      const updatedCategories = homepageContent.equipment_categories.filter(cat => cat.id !== catId);
      await axios.put(`${API}/settings/homepage`, {
        equipment_categories: updatedCategories
      }, { headers: { Authorization: adminAuth } });
      
      setHomepageContent(prev => ({ ...prev, equipment_categories: updatedCategories }));
      toast.success("Category deleted!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete category");
    } finally {
      setSavingHomepage(false);
    }
  };

  const isManager = currentUser?.role === "manager";

  const productCategoryOptions = Array.from(new Set([
    ...products.map(p => p.category).filter(Boolean),
    ...(homepageContent.equipment_categories || []).map(cat => cat.key).filter(Boolean),
    productForm.category
  ])).sort((a, b) => a.localeCompare(b));

  // Fetch detailed analytics with date range
  const fetchDetailedAnalytics = useCallback(async (range, customStart, customEnd) => {
    setAnalyticsLoading(true);
    try {
      let startDate, endDate;
      const now = new Date();
      
      if (range === "week") {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        endDate = now;
      } else if (range === "month") {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        endDate = now;
      } else if (range === "3months") {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        endDate = now;
      } else if (range === "6months") {
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 6);
        endDate = now;
      } else if (range === "year") {
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        endDate = now;
      } else if (range === "all") {
        startDate = null;
        endDate = null;
      } else if (range === "custom" && customStart && customEnd) {
        startDate = new Date(customStart);
        endDate = new Date(customEnd);
      }
      
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate.toISOString().split('T')[0]);
      if (endDate) params.append("end_date", endDate.toISOString().split('T')[0]);
      
      const res = await axios.get(`${API}/analytics/detailed?${params.toString()}`, {
        headers: { Authorization: adminAuth }
      });
      setDetailedAnalytics(res.data);
    } catch (e) {
      console.error("Failed to fetch analytics:", e);
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [adminAuth]);

  // Fetch analytics when tab changes or range changes
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchDetailedAnalytics(analyticsRange, analyticsStartDate, analyticsEndDate);
    }
  }, [activeTab, analyticsRange, fetchDetailedAnalytics]);

  const handleAnalyticsRangeChange = (newRange) => {
    setAnalyticsRange(newRange);
    if (newRange !== "custom") {
      setAnalyticsStartDate("");
      setAnalyticsEndDate("");
    }
  };

  const applyCustomDateRange = () => {
    if (analyticsStartDate && analyticsEndDate) {
      fetchDetailedAnalytics("custom", analyticsStartDate, analyticsEndDate);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/products/${id}`, { headers: { Authorization: adminAuth } }); toast.success("Deleted!"); fetchData(); } catch (e) { toast.error("Failed"); }
  };

  const reorderProducts = async (nextProducts) => {
    setProducts(nextProducts);
    try {
      await axios.put(
        `${API}/products/reorder/list`,
        { product_ids: nextProducts.map((product) => product.id) },
        { headers: { Authorization: adminAuth } }
      );
      toast.success("Product order updated");
    } catch (e) {
      toast.error("Failed to update product order");
      fetchData();
    }
  };

  const moveProduct = (productId, direction) => {
    const currentIndex = products.findIndex((product) => product.id === productId);
    if (currentIndex < 0) return;
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= products.length) return;

    const reorderedProducts = [...products];
    const [movedProduct] = reorderedProducts.splice(currentIndex, 1);
    reorderedProducts.splice(targetIndex, 0, movedProduct);
    reorderProducts(reorderedProducts);
  };

  const exportBookings = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/bookings/export/excel`, {
        headers: { Authorization: adminAuth },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Bookings exported successfully!");
    } catch (e) {
      toast.error("Failed to export bookings");
    } finally {
      setExporting(false);
    }
  };

  const downloadInvoice = async (bookingId, bookingIdDisplay) => {
    try {
      toast.loading("Generating invoice...", { id: "invoice" });
      const response = await axios.get(`${API}/bookings/${bookingId}/invoice`, {
        headers: { Authorization: adminAuth },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${bookingIdDisplay}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded!", { id: "invoice" });
    } catch (e) {
      toast.error("Failed to download invoice", { id: "invoice" });
    }
  };


  const openAdjustInvoiceDialog = (booking) => {
    setInvoiceAdjustForm({
      booking,
      discount_amount: booking?.invoice_discount || 0,
      notes: booking?.invoice_adjustment_notes || "",
      resend_email: true,
    });
    setInvoiceAdjustDialog(true);
  };

  const submitInvoiceAdjustment = async (e) => {
    e.preventDefault();
    if (!invoiceAdjustForm.booking?.id) {
      toast.error("No booking selected");
      return;
    }

    const discount = Number(invoiceAdjustForm.discount_amount);
    if (Number.isNaN(discount) || discount < 0) {
      toast.error("Please enter a valid non-negative discount amount");
      return;
    }

    setSavingInvoiceAdjust(true);
    try {
      toast.loading("Updating invoice...", { id: "invoice-adjust" });
      await axios.put(`${API}/bookings/${invoiceAdjustForm.booking.id}/invoice-adjustment`, {
        discount_amount: discount,
        notes: invoiceAdjustForm.notes || null,
        resend_email: invoiceAdjustForm.resend_email,
      }, { headers: { Authorization: adminAuth } });

      toast.success(invoiceAdjustForm.resend_email ? "Invoice updated and sent to customer" : "Invoice updated", { id: "invoice-adjust" });
      setInvoiceAdjustDialog(false);
      setBookingDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update invoice", { id: "invoice-adjust" });
    } finally {
      setSavingInvoiceAdjust(false);
    }
  };

  const handleCleanup = async () => {
    // Validate based on mode
    if (cleanupMode === "before" && !cleanupDate) {
      toast.error("Please select a date");
      return;
    }
    if (cleanupMode === "after" && !cleanupDate) {
      toast.error("Please select a date");
      return;
    }
    if (cleanupMode === "between" && (!cleanupStartDate || !cleanupEndDate)) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    try {
      const payload = { mode: cleanupMode };
      if (cleanupMode === "before") {
        payload.before_date = cleanupDate;
      } else if (cleanupMode === "after") {
        payload.after_date = cleanupDate;
      } else if (cleanupMode === "between") {
        payload.start_date = cleanupStartDate;
        payload.end_date = cleanupEndDate;
      }
      
      const response = await axios.post(`${API}/bookings/cleanup`, 
        payload,
        { headers: { Authorization: adminAuth } }
      );
      toast.success(response.data.message);
      setCleanupConfirm(false);
      setCleanupDialog(false);
      setCleanupDate("");
      setCleanupStartDate("");
      setCleanupEndDate("");
      setCleanupMode("before");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cleanup bookings");
    }
  };

  const getCleanupDescription = () => {
    if (cleanupMode === "before") return `before ${cleanupDate}`;
    if (cleanupMode === "after") return `after ${cleanupDate}`;
    if (cleanupMode === "between") return `between ${cleanupStartDate} and ${cleanupEndDate}`;
    return "";
  };

  const isCleanupValid = () => {
    if (cleanupMode === "before" || cleanupMode === "after") return !!cleanupDate;
    if (cleanupMode === "between") return cleanupStartDate && cleanupEndDate;
    return false;
  };

  const filtered = statusFilter === "all" ? bookings : bookings.filter(b => b.status === statusFilter);
  const pending = bookings.filter(b => b.status === "pending").length;
  const approved = bookings.filter(b => b.status === "approved").length;
  const rejected = bookings.filter(b => b.status === "rejected").length;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-sage" /></div>;

  return (
    <div className="min-h-screen bg-cream">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-midnight text-white p-4 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-sage rounded-lg flex items-center justify-center"><PartyPopper className="w-4 h-4" /></div>
          <span className="font-heading font-bold">Admin</span>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
          className="p-2 rounded-lg hover:bg-white/10"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop fixed, Mobile slide-in */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-midnight text-white p-6 z-50 transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-sage rounded-xl flex items-center justify-center"><PartyPopper className="w-5 h-5" /></div>
          <span className="font-heading font-bold text-lg">Admin</span>
        </div>
        <nav className="space-y-2">
          <button data-testid="nav-bookings" onClick={() => { setActiveTab("bookings"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "bookings" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
            <Package className="w-5 h-5" />Bookings{pending > 0 && <span className="ml-auto bg-white text-sage text-xs font-bold px-2 py-0.5 rounded-full">{pending}</span>}
          </button>
          <button data-testid="nav-calendar" onClick={() => { setActiveTab("calendar"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "calendar" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
            <Calendar className="w-5 h-5" />Calendar
          </button>
          <button data-testid="nav-products" onClick={() => { setActiveTab("products"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "products" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
            <TrendingUp className="w-5 h-5" />Products
          </button>
          <button data-testid="nav-analytics" onClick={() => { setActiveTab("analytics"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "analytics" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
            <BarChart3 className="w-5 h-5" />Analytics
          </button>
          {isManager && (
            <button data-testid="nav-users" onClick={() => { setActiveTab("users"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "users" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
              <Users className="w-5 h-5" />Team
            </button>
          )}
          <button data-testid="nav-settings" onClick={() => { setActiveTab("settings"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "settings" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
            <Settings className="w-5 h-5" />Settings
          </button>
          {isManager && (
            <button data-testid="nav-content" onClick={() => { setActiveTab("content"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "content" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
              <Package className="w-5 h-5" />Content
            </button>
          )}
          {isManager && (
            <button data-testid="nav-system" onClick={() => { setActiveTab("system"); setMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${activeTab === "system" ? "bg-sage text-white shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}>
              <Server className="w-5 h-5" />System
            </button>
          )}
        </nav>
        <div className="absolute bottom-6 left-6 right-6 space-y-3">
          {currentUser && (
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-sm text-white/80 truncate">{currentUser.email}</p>
              <p className="text-xs text-white/50 flex items-center gap-1">
                {currentUser.role === "manager" ? <Crown className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                {currentUser.role}
              </p>
            </div>
          )}
          <button data-testid="logout-btn" onClick={() => { localStorage.removeItem("adminAuth"); localStorage.removeItem("adminUser"); navigate("/admin"); }} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white/50 hover:bg-white/10">
            <LogOut className="w-5 h-5" />Logout
          </button>
        </div>
      </aside>

      {/* Main Content - responsive margin */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Analytics Cards - responsive grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <StatCard label="Pending" value={pending} color="text-amber-600" />
          <StatCard label="Approved" value={approved} color="text-green-600" />
          <StatCard label="Revenue" value={`$${analytics?.total_revenue?.toFixed(0) || '0'}`} color="text-sage" />
          <StatCard label="Products" value={products.length} color="text-charcoal" />
        </div>

        {activeTab === "bookings" ? (
          <BookingsTable bookings={filtered} statusFilter={statusFilter} setStatusFilter={setStatusFilter} onView={(b) => { setSelectedBooking(b); setBookingDialog(true); }} onApprove={(id) => updateStatus(id, "approved")} onReject={(id) => updateStatus(id, "rejected")} onExport={exportBookings} exporting={exporting} onCleanup={() => setCleanupDialog(true)} isManager={isManager} onDownloadInvoice={downloadInvoice} />
        ) : activeTab === "calendar" ? (
          <CalendarView bookings={bookings} calendarDate={calendarDate} setCalendarDate={setCalendarDate} onBookingClick={(b) => { setSelectedBooking(b); setBookingDialog(true); }} />
        ) : activeTab === "users" && isManager ? (
          <UsersPanel 
            users={users}
            currentUser={currentUser}
            onAdd={() => openUserForm()}
            onEdit={openUserForm}
            onDelete={(user) => setDeleteUserDialog(user)}
            onToggleActive={toggleUserActive}
          />
        ) : activeTab === "system" && isManager ? (
          <SystemConfigPanel 
            config={systemConfig}
            setConfig={setSystemConfig}
            onSave={saveSystemConfig}
            saving={savingSystem}
          />
        ) : activeTab === "settings" ? (
          <SettingsPanel 
            maintenanceMode={maintenanceMode}
            maintenanceMessage={maintenanceMessage}
            setMaintenanceMessage={setMaintenanceMessage}
            toggleMaintenanceMode={toggleMaintenanceMode}
            savingMaintenance={savingMaintenance}
            vacationMode={vacationMode}
            vacationStartDate={vacationStartDate}
            vacationEndDate={vacationEndDate}
            setVacationStartDate={setVacationStartDate}
            setVacationEndDate={setVacationEndDate}
            toggleVacationMode={toggleVacationMode}
            saveVacationDates={saveVacationDates}
            savingVacation={savingVacation}
            instagramUrl={instagramUrl}
            facebookUrl={facebookUrl}
            tiktokUrl={tiktokUrl}
            tiktokEnabled={tiktokEnabled}
            setInstagramUrl={setInstagramUrl}
            setFacebookUrl={setFacebookUrl}
            setTiktokUrl={setTiktokUrl}
            setTiktokEnabled={setTiktokEnabled}
            saveSocialMedia={saveSocialMedia}
            savingSocial={savingSocial}
            isManager={isManager}
          />
        ) : activeTab === "content" && isManager ? (
          <HomepageContentPanel
            content={homepageContent}
            setContent={setHomepageContent}
            saveHeroImage={saveHeroImage}
            savingHomepage={savingHomepage}
            onAddCategory={() => openCategoryForm()}
            onEditCategory={openCategoryForm}
            onDeleteCategory={deleteCategory}
            saveHeroText={saveHeroText}
            saveDeliveryFee={saveDeliveryFee}
            isManager={isManager}
          />
        ) : activeTab === "analytics" ? (
          <AnalyticsPanel
            analytics={detailedAnalytics}
            loading={analyticsLoading}
            range={analyticsRange}
            setRange={handleAnalyticsRangeChange}
            startDate={analyticsStartDate}
            endDate={analyticsEndDate}
            setStartDate={setAnalyticsStartDate}
            setEndDate={setAnalyticsEndDate}
            onApplyCustomRange={applyCustomDateRange}
          />
        ) : (
          <ProductsTable products={products} onAdd={() => openProductForm()} onEdit={openProductForm} onDelete={deleteProduct} onMoveProduct={moveProduct} isManager={isManager} />
        )}
      </main>

      <ProductDialog open={productDialog} onClose={() => setProductDialog(false)} form={productForm} setForm={setProductForm} onSubmit={saveProduct} isEdit={!!editingProduct} categoryOptions={productCategoryOptions} />
      <BookingDialog open={bookingDialog} onClose={() => setBookingDialog(false)} booking={selectedBooking} products={products} onApprove={() => updateStatus(selectedBooking?.id, "approved")} onReject={() => updateStatus(selectedBooking?.id, "rejected")} isManager={isManager} onDownloadInvoice={downloadInvoice} onAdjustInvoice={openAdjustInvoiceDialog} onSaveEdit={updateBookingDetails} savingEdit={savingBookingEdit} />
      <InvoiceAdjustmentDialog
        open={invoiceAdjustDialog}
        onClose={() => setInvoiceAdjustDialog(false)}
        form={invoiceAdjustForm}
        setForm={setInvoiceAdjustForm}
        onSubmit={submitInvoiceAdjustment}
        saving={savingInvoiceAdjust}
      />
      
      {/* User Dialog */}
      <UserDialog 
        open={userDialog} 
        onClose={() => setUserDialog(false)} 
        form={userForm} 
        setForm={setUserForm} 
        onSubmit={saveUser} 
        isEdit={!!editingUser} 
        saving={savingUser}
      />
      
      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deleteUserDialog} onOpenChange={() => setDeleteUserDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-charcoal/80">
              Are you sure you want to delete <strong>{deleteUserDialog?.name}</strong> ({deleteUserDialog?.email})?
            </p>
            <p className="mt-3 text-red-600 font-medium text-sm">
              This action cannot be undone!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialog(null)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteUser(deleteUserDialog?.id)}
              data-testid="confirm-delete-user-btn"
            >
              Yes, Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Category Dialog for Homepage Content */}
      <Dialog open={categoryDialog} onOpenChange={() => setCategoryDialog(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingCategory ? "Edit" : "Add"} Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCategory} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (English) *</Label>
                <Input 
                  value={categoryForm.title_en} 
                  onChange={(e) => setCategoryForm(f => ({ ...f, title_en: e.target.value }))} 
                  placeholder="e.g., Chairs"
                  required 
                  data-testid="category-title-en"
                />
              </div>
              <div>
                <Label>Title (Spanish) *</Label>
                <Input 
                  value={categoryForm.title_es} 
                  onChange={(e) => setCategoryForm(f => ({ ...f, title_es: e.target.value }))} 
                  placeholder="e.g., Sillas"
                  required 
                  data-testid="category-title-es"
                />
              </div>
            </div>
            <div>
              <Label>Key (unique identifier) *</Label>
              <Input 
                value={categoryForm.key} 
                onChange={(e) => setCategoryForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} 
                placeholder="e.g., chairs, tables, bouncy_castles"
                required 
                data-testid="category-key"
              />
              <p className="text-xs text-charcoal/50 mt-1">Used for internal identification. Use lowercase with underscores.</p>
            </div>
            <div>
              <Label>Description (English) *</Label>
              <Textarea 
                value={categoryForm.description_en} 
                onChange={(e) => setCategoryForm(f => ({ ...f, description_en: e.target.value }))} 
                placeholder="e.g., Elegant seating for any occasion"
                rows={2}
                required 
                data-testid="category-desc-en"
              />
            </div>
            <div>
              <Label>Description (Spanish) *</Label>
              <Textarea 
                value={categoryForm.description_es} 
                onChange={(e) => setCategoryForm(f => ({ ...f, description_es: e.target.value }))} 
                placeholder="e.g., Asientos elegantes para cualquier ocasión"
                rows={2}
                required 
                data-testid="category-desc-es"
              />
            </div>
            <div>
              <Label>Image URL *</Label>
              <Input 
                type="url"
                value={categoryForm.image_url} 
                onChange={(e) => setCategoryForm(f => ({ ...f, image_url: e.target.value }))} 
                placeholder="https://..."
                required 
                data-testid="category-image-url"
              />
              {categoryForm.image_url && (
                <div className="mt-2 w-24 h-24 rounded-lg overflow-hidden border border-charcoal/10">
                  <img 
                    src={categoryForm.image_url} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display='none'}
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Link URL</Label>
              <Input 
                value={categoryForm.link} 
                onChange={(e) => setCategoryForm(f => ({ ...f, link: e.target.value }))} 
                placeholder="/products"
                data-testid="category-link"
              />
              <p className="text-xs text-charcoal/50 mt-1">Where to redirect when clicked. Default: /products</p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setCategoryDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={savingHomepage} className="bg-sage hover:bg-sage/90 text-white" data-testid="save-category-btn">
                {savingHomepage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingCategory ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Cleanup Dialog */}
      <Dialog open={cleanupDialog} onOpenChange={(open) => { setCleanupDialog(open); if (!open) { setCleanupMode("before"); setCleanupDate(""); setCleanupStartDate(""); setCleanupEndDate(""); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Cleanup Booking History
            </DialogTitle>
            <DialogDescription>
              Delete booking records based on their creation date. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Mode Selection */}
            <div>
              <Label className="mb-2 block">Delete Mode</Label>
              <Select value={cleanupMode} onValueChange={setCleanupMode}>
                <SelectTrigger data-testid="cleanup-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">Before a date</SelectItem>
                  <SelectItem value="after">After a date</SelectItem>
                  <SelectItem value="between">Between dates (range)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Inputs based on mode */}
            {(cleanupMode === "before" || cleanupMode === "after") && (
              <div>
                <Label htmlFor="cleanup-date">
                  {cleanupMode === "before" ? "Delete bookings created before:" : "Delete bookings created after:"}
                </Label>
                <Input
                  id="cleanup-date"
                  type="date"
                  value={cleanupDate}
                  onChange={(e) => setCleanupDate(e.target.value)}
                  className="mt-2"
                  data-testid="cleanup-date-picker"
                />
              </div>
            )}
            
            {cleanupMode === "between" && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="cleanup-start-date">Start Date (inclusive)</Label>
                  <Input
                    id="cleanup-start-date"
                    type="date"
                    value={cleanupStartDate}
                    onChange={(e) => setCleanupStartDate(e.target.value)}
                    className="mt-2"
                    data-testid="cleanup-start-date-picker"
                  />
                </div>
                <div>
                  <Label htmlFor="cleanup-end-date">End Date (inclusive)</Label>
                  <Input
                    id="cleanup-end-date"
                    type="date"
                    value={cleanupEndDate}
                    onChange={(e) => setCleanupEndDate(e.target.value)}
                    className="mt-2"
                    data-testid="cleanup-end-date-picker"
                  />
                </div>
              </div>
            )}
            
            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This will permanently delete all bookings {cleanupMode === "before" ? "created before the selected date" : cleanupMode === "after" ? "created on or after the selected date" : "created within the selected date range"}.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => setCleanupConfirm(true)}
              disabled={!isCleanupValid()}
              data-testid="cleanup-proceed-btn"
            >
              Proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupConfirm} onOpenChange={setCleanupConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-charcoal/80">
              Are you sure you want to <strong>permanently delete</strong> all bookings created <strong>{getCleanupDescription()}</strong>?
            </p>
            <p className="mt-3 text-red-600 font-medium text-sm">
              This action cannot be undone!
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleCleanup}
              data-testid="cleanup-confirm-btn"
            >
              Yes, Delete Bookings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm">
    <p className="text-charcoal/60 text-xs sm:text-sm mb-1">{label}</p>
    <p className={`font-heading font-bold text-xl sm:text-3xl ${color}`}>{value}</p>
  </div>
);

// Users Panel Component
const UsersPanel = ({ users, currentUser, onAdd, onEdit, onDelete, onToggleActive }) => (
  <div className="bg-white rounded-3xl shadow-sm">
    <div className="p-4 sm:p-6 border-b border-charcoal/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
      <div>
        <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Team Management</h2>
        <p className="text-sm text-charcoal/60 mt-1">Manage admin portal users and their roles</p>
      </div>
      <Button data-testid="add-user-btn" onClick={onAdd} className="bg-sage hover:bg-sage/90 text-white rounded-full">
        <UserPlus className="w-4 h-4 sm:mr-2" />
        <span className="hidden sm:inline">Add User</span>
      </Button>
    </div>
    
    <div className="p-4 sm:p-6">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="font-medium text-blue-800 text-sm sm:text-base">Role Permissions</span>
        </div>
        <div className="mt-2 text-xs sm:text-sm text-blue-700 space-y-1 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-2">
          <div><strong>Manager:</strong> Full access</div>
          <div><strong>Viewer:</strong> Read-only access</div>
        </div>
      </div>
      
      <div className="space-y-3">
        {users.length === 0 ? (
          <div className="text-center py-8 text-charcoal/60">No users found</div>
        ) : users.map(user => (
          <div 
            key={user.id} 
            data-testid={`user-row-${user.id}`}
            className={`border rounded-xl p-3 sm:p-4 ${!user.is_active ? 'bg-charcoal/5 border-charcoal/10' : 'border-charcoal/10'}`}
          >
            {/* Mobile Layout */}
            <div className="sm:hidden space-y-3">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${user.role === 'manager' ? 'bg-amber-100' : 'bg-sage/10'}`}>
                  {user.role === 'manager' ? <Crown className="w-5 h-5 text-amber-600" /> : <UserCheck className="w-5 h-5 text-sage" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-charcoal truncate">{user.name}</p>
                    {!user.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>}
                    {user.id === currentUser?.id && <span className="text-xs bg-sage/20 text-sage px-2 py-0.5 rounded-full">You</span>}
                  </div>
                  <p className="text-sm text-charcoal/60 truncate">{user.email}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${user.role === 'manager' ? 'bg-amber-100 text-amber-800' : 'bg-sage/10 text-sage'}`}>
                    {user.role}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-charcoal/10">
                <Button variant="outline" size="sm" className="flex-1" data-testid={`edit-user-mobile-${user.id}`} onClick={() => onEdit(user)} disabled={user.id === currentUser?.id}>
                  <Edit className="w-4 h-4 mr-1" />Edit
                </Button>
                <Button variant="outline" size="sm" data-testid={`toggle-user-mobile-${user.id}`} onClick={() => onToggleActive(user)} disabled={user.id === currentUser?.id} className={user.is_active ? "text-amber-600" : "text-green-600"}>
                  {user.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="sm" className="text-red-500 border-red-200" data-testid={`delete-user-mobile-${user.id}`} onClick={() => onDelete(user)} disabled={user.id === currentUser?.id}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Desktop Layout */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.role === 'manager' ? 'bg-amber-100' : 'bg-sage/10'}`}>
                  {user.role === 'manager' ? <Crown className="w-5 h-5 text-amber-600" /> : <UserCheck className="w-5 h-5 text-sage" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-charcoal">{user.name}</p>
                    {!user.is_active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>}
                    {user.id === currentUser?.id && <span className="text-xs bg-sage/20 text-sage px-2 py-0.5 rounded-full">You</span>}
                  </div>
                  <p className="text-sm text-charcoal/60">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${user.role === 'manager' ? 'bg-amber-100 text-amber-800' : 'bg-sage/10 text-sage'}`}>
                    {user.role}
                  </span>
                  {user.last_login && <p className="text-xs text-charcoal/50 mt-1">Last login: {new Date(user.last_login).toLocaleDateString()}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" data-testid={`edit-user-${user.id}`} onClick={() => onEdit(user)} disabled={user.id === currentUser?.id}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`toggle-user-${user.id}`} onClick={() => onToggleActive(user)} disabled={user.id === currentUser?.id} className={user.is_active ? "text-amber-600" : "text-green-600"}>{user.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}</Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-user-${user.id}`} className="text-red-500" onClick={() => onDelete(user)} disabled={user.id === currentUser?.id}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// User Dialog Component
const UserDialog = ({ open, onClose, form, setForm, onSubmit, isEdit, saving }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2">
          {isEdit ? <Edit className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
          {isEdit ? "Edit User" : "Add New User"}
        </DialogTitle>
        <DialogDescription>
          {isEdit ? "Update user information and role." : "Create a new admin portal user."}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label>Name *</Label>
          <Input 
            data-testid="user-name-input" 
            value={form.name} 
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
            required 
            placeholder="John Doe" 
          />
        </div>
        <div>
          <Label>Email *</Label>
          <Input 
            type="email"
            data-testid="user-email-input" 
            value={form.email} 
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} 
            required 
            placeholder="john@example.com"
            disabled={isEdit}
          />
          {isEdit && <p className="text-xs text-charcoal/50 mt-1">Email cannot be changed</p>}
        </div>
        {!isEdit && (
          <div>
            <Label>Password *</Label>
            <Input 
              type="password"
              data-testid="user-password-input" 
              value={form.password} 
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} 
              required 
              placeholder="Minimum 8 characters"
              minLength={8}
            />
          </div>
        )}
        <div>
          <Label>Role *</Label>
          <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
            <SelectTrigger data-testid="user-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  <span>Viewer</span>
                  <span className="text-xs text-charcoal/50">- Read only</span>
                </div>
              </SelectItem>
              <SelectItem value="manager">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  <span>Manager</span>
                  <span className="text-xs text-charcoal/50">- Full access</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            data-testid="save-user-btn" 
            className="bg-sage hover:bg-sage/90 text-white"
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Update" : "Create"} User
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

// System Configuration Panel Component
const SystemConfigPanel = ({ config, setConfig, onSave, saving }) => {
  const [newApiKey, setNewApiKey] = useState("");
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
        <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-purple-100 flex-shrink-0">
            <Server className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">System Configuration</h2>
            <p className="text-xs sm:text-sm text-charcoal/60">Manage email settings and API keys.</p>
          </div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="font-medium text-amber-800 text-sm sm:text-base">Secure Configuration</span>
          </div>
          <p className="text-xs sm:text-sm text-amber-700 mt-1">Settings stored securely in database.</p>
        </div>

        {/* Resend API Key */}
        <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Key className="w-4 h-4 sm:w-5 sm:h-5 text-charcoal/60" />
            <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Email API Key (Resend)</h3>
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs sm:text-sm text-charcoal/60">Current Key:</span>
              <code className="bg-charcoal/5 px-2 py-1 rounded text-xs sm:text-sm font-mono break-all">
                {config.resend_api_key_configured ? config.resend_api_key_masked : "Not configured"}
              </code>
              {config.resend_api_key_configured && (
                <span className="text-green-600 text-xs sm:text-sm font-medium">✓ Active</span>
              )}
            </div>
            
            {!showApiKeyInput ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowApiKeyInput(true)}
                className="mt-2"
              >
                Update API Key
              </Button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <Input
                  type="password"
                  placeholder="Enter new Resend API key (re_...)"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  className="rounded-xl font-mono text-sm flex-1"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (newApiKey.trim()) {
                        onSave("resend_api_key", newApiKey.trim());
                        setNewApiKey("");
                        setShowApiKeyInput(false);
                      }
                    }}
                    disabled={saving || !newApiKey.trim()}
                    className="bg-sage hover:bg-sage/90 text-white flex-1 sm:flex-none"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowApiKeyInput(false); setNewApiKey(""); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Email Settings */}
        <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-charcoal/60" />
            <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Email Settings</h3>
          </div>
          
          <div className="space-y-4">
            {/* Sender Email */}
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Sender Email (From)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="text"
                  value={config.sender_email}
                  onChange={(e) => setConfig({ ...config, sender_email: e.target.value })}
                  placeholder="Magnolia Events <alex@mail.magnolia-events.com>"
                  className="rounded-xl flex-1 text-sm"
                  data-testid="sender-email"
                />
                <Button
                  onClick={() => onSave("sender_email", config.sender_email)}
                  disabled={saving}
                  className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
              <p className="text-xs text-charcoal/50 mt-1">The name and email shown as the sender.</p>
            </div>

            {/* Reply-To Email */}
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Reply-To Email</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={config.reply_to_email}
                  onChange={(e) => setConfig({ ...config, reply_to_email: e.target.value })}
                  placeholder="alex@magnolia-events.com"
                  className="rounded-xl flex-1 text-sm"
                  data-testid="reply-to-email"
                />
                <Button
                  onClick={() => onSave("reply_to_email", config.reply_to_email)}
                  disabled={saving}
                  className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
              <p className="text-xs text-charcoal/50 mt-1">When customers reply to emails, they'll reach this address.</p>
            </div>

            {/* Admin Email */}
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Admin Email</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={config.admin_email}
                  onChange={(e) => setConfig({ ...config, admin_email: e.target.value })}
                  placeholder="alex@magnolia-events.com"
                  className="rounded-xl flex-1 text-sm"
                  data-testid="admin-email"
                />
                <Button
                  onClick={() => onSave("admin_email", config.admin_email)}
                  disabled={saving}
                  className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
              <p className="text-xs text-charcoal/50 mt-1">Default admin email for system notifications.</p>
            </div>

            {/* Booking Notification Emails */}
            <div className="pt-4 border-t border-charcoal/10">
              <Label className="text-charcoal/70 mb-2 block font-medium text-sm">Booking Notification Emails</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Textarea
                  value={config.booking_notification_emails || ""}
                  onChange={(e) => setConfig({ ...config, booking_notification_emails: e.target.value })}
                  placeholder="alex@magnolia-events.com, valeria@magnolia-events.com"
                  className="rounded-xl flex-1 min-h-[80px] text-sm"
                  data-testid="booking-notification-emails"
                />
                <Button
                  onClick={() => onSave("booking_notification_emails", config.booking_notification_emails)}
                  disabled={saving}
                  className="bg-sage hover:bg-sage/90 text-white sm:self-start w-full sm:w-auto"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              </div>
              <p className="text-xs text-charcoal/50 mt-1">
                Email addresses separated by commas for booking notifications.
              </p>
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Customers only receive emails when booking is approved or rejected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsPanel = ({ 
  maintenanceMode, maintenanceMessage, setMaintenanceMessage, toggleMaintenanceMode, savingMaintenance,
  vacationMode, vacationStartDate, vacationEndDate, setVacationStartDate, setVacationEndDate, toggleVacationMode, saveVacationDates, savingVacation,
  instagramUrl, facebookUrl, tiktokUrl, tiktokEnabled, setInstagramUrl, setFacebookUrl, setTiktokUrl, setTiktokEnabled, saveSocialMedia, savingSocial,
  isManager
}) => (
  <div className="space-y-4 sm:space-y-6">
    <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
      <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal mb-4 sm:mb-6">Site Settings</h2>
      
      {!isManager && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="font-medium text-amber-800 text-sm sm:text-base">View Only</span>
          </div>
          <p className="text-xs sm:text-sm text-amber-700 mt-1">You have viewer access. Contact a manager to make changes.</p>
        </div>
      )}
      
      {/* Maintenance Mode Toggle */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${maintenanceMode ? 'bg-amber-100' : 'bg-sage/10'}`}>
              <Construction className={`w-5 h-5 sm:w-6 sm:h-6 ${maintenanceMode ? 'text-amber-600' : 'text-sage'}`} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Maintenance Mode</h3>
              <p className="text-xs sm:text-sm text-charcoal/60 mt-1">
                When enabled, visitors see a "Coming Soon" page. Admin portal remains accessible.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <span className={`text-xs sm:text-sm font-medium ${maintenanceMode ? 'text-amber-600' : 'text-charcoal/50'}`}>
              {maintenanceMode ? 'ON' : 'OFF'}
            </span>
            <Switch 
              checked={maintenanceMode}
              onCheckedChange={toggleMaintenanceMode}
              disabled={savingMaintenance || !isManager}
              data-testid="maintenance-toggle"
            />
          </div>
        </div>
        
        {maintenanceMode && (
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-charcoal/10">
            <Label className="text-charcoal/70 mb-2 block text-sm">Maintenance Message</Label>
            <Textarea
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              placeholder="We're getting things ready. Please check back soon."
              className="rounded-xl resize-none text-sm"
              rows={2}
              data-testid="maintenance-message"
            />
            <p className="text-xs text-charcoal/50 mt-2">This message will be shown to visitors during maintenance.</p>
          </div>
        )}
      </div>
      
      {maintenanceMode && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800 text-sm sm:text-base">Maintenance Mode is Active</p>
            <p className="text-xs sm:text-sm text-amber-700">Visitors cannot access the public website or make bookings.</p>
          </div>
        </div>
      )}

      {/* Vacation Mode Toggle */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${vacationMode ? 'bg-blue-100' : 'bg-sage/10'}`}>
              <Palmtree className={`w-5 h-5 sm:w-6 sm:h-6 ${vacationMode ? 'text-blue-600' : 'text-sage'}`} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Vacation Mode</h3>
              <p className="text-xs sm:text-sm text-charcoal/60 mt-1">
                Block bookings during a specific date range.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-end sm:self-auto">
            <span className={`text-xs sm:text-sm font-medium ${vacationMode ? 'text-blue-600' : 'text-charcoal/50'}`}>
              {vacationMode ? 'ON' : 'OFF'}
            </span>
            <Switch 
              checked={vacationMode}
              onCheckedChange={toggleVacationMode}
              disabled={savingVacation || !isManager}
              data-testid="vacation-toggle"
            />
          </div>
        </div>
        
        {/* Date Range Selection - Always show when vacation mode panel is visible */}
        <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-charcoal/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Vacation Start Date</Label>
              <Input
                type="date"
                value={vacationStartDate}
                onChange={(e) => setVacationStartDate(e.target.value)}
                className="rounded-xl text-sm"
                data-testid="vacation-start-date"
              />
            </div>
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Vacation End Date</Label>
              <Input
                type="date"
                value={vacationEndDate}
                onChange={(e) => setVacationEndDate(e.target.value)}
                className="rounded-xl text-sm"
                data-testid="vacation-end-date"
              />
            </div>
          </div>
          {vacationMode && vacationStartDate && vacationEndDate && (
            <Button 
              onClick={saveVacationDates}
              disabled={savingVacation}
              className="mt-4 bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
              data-testid="save-vacation-dates"
            >
              {savingVacation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update Dates
            </Button>
          )}
          <p className="text-xs text-charcoal/50 mt-3">
            {vacationMode 
              ? "Bookings that overlap with these dates will be blocked."
              : "Select dates and turn on vacation mode to block bookings during this period."
            }
          </p>
        </div>
      </div>
      
      {vacationMode && vacationStartDate && vacationEndDate && (
        <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <Palmtree className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800 text-sm sm:text-base">Vacation Mode is Active</p>
            <p className="text-xs sm:text-sm text-blue-700">
              Bookings from <strong>{vacationStartDate}</strong> to <strong>{vacationEndDate}</strong> are blocked.
            </p>
          </div>
        </div>
      )}

      {/* Social Media Links */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mt-4 sm:mt-6">
        <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-pink-100 flex-shrink-0">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </div>
          <div>
            <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Social Media Links</h3>
            <p className="text-xs sm:text-sm text-charcoal/60 mt-1">
              Manage social URLs shown in header and footer.
            </p>
          </div>
        </div>
        
        <div className="space-y-3 sm:space-y-4">
          <div>
            <Label className="text-charcoal/70 mb-2 block text-sm">Instagram URL</Label>
            <Input
              type="url"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              placeholder="https://www.instagram.com/yourusername"
              className="rounded-xl text-sm"
              data-testid="instagram-url"
            />
          </div>
          <div>
            <Label className="text-charcoal/70 mb-2 block text-sm">Facebook URL</Label>
            <Input
              type="url"
              value={facebookUrl}
              onChange={(e) => setFacebookUrl(e.target.value)}
              placeholder="https://www.facebook.com/yourpage"
              className="rounded-xl text-sm"
              data-testid="facebook-url"
            />
          </div>
          
          {/* TikTok Section */}
          <div className="border-t border-charcoal/10 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <div>
                  <Label className="text-charcoal font-medium">TikTok</Label>
                  <p className="text-xs text-charcoal/50">Show TikTok link on main page</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${tiktokEnabled ? 'text-sage' : 'text-charcoal/50'}`}>
                  {tiktokEnabled ? 'Visible' : 'Hidden'}
                </span>
                <Switch
                  checked={tiktokEnabled}
                  onCheckedChange={setTiktokEnabled}
                  disabled={!isManager}
                  data-testid="tiktok-toggle"
                />
              </div>
            </div>
            <Input
              type="url"
              value={tiktokUrl}
              onChange={(e) => setTiktokUrl(e.target.value)}
              placeholder="https://www.tiktok.com/@yourusername"
              className="rounded-xl text-sm"
              disabled={!isManager}
              data-testid="tiktok-url"
            />
            <p className="text-xs text-charcoal/50 mt-1">
              {tiktokEnabled ? "TikTok link will appear in header and footer" : "Enable toggle to show TikTok on your website"}
            </p>
          </div>
          
          <Button 
            onClick={saveSocialMedia}
            disabled={savingSocial}
            className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
            data-testid="save-social-btn"
          >
            {savingSocial ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Social Links
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// Homepage Content Panel Component
const HomepageContentPanel = ({ 
  content, setContent, saveHeroImage, savingHomepage,
  onAddCategory, onEditCategory, onDeleteCategory, saveHeroText, saveDeliveryFee, isManager
}) => (
  <div className="space-y-4 sm:space-y-6">
    <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
      <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-emerald-100 flex-shrink-0">
          <Package className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Homepage Content</h2>
          <p className="text-xs sm:text-sm text-charcoal/60">Manage hero section and equipment categories on the main page.</p>
        </div>
      </div>

      {!isManager && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <span className="font-medium text-amber-800 text-sm sm:text-base">View Only</span>
          </div>
          <p className="text-xs sm:text-sm text-amber-700 mt-1">You have viewer access. Contact a manager to make changes.</p>
        </div>
      )}

      {/* Hero Text Section */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
        <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal mb-4">Hero Text</h3>
        <p className="text-xs sm:text-sm text-charcoal/60 mb-4">The main headline and description shown on the homepage.</p>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Title (English) *</Label>
              <Input
                value={content.hero_title_en || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_title_en: e.target.value }))}
                placeholder="Make Your Event Unforgettable"
                className="rounded-xl text-sm"
                disabled={!isManager}
                data-testid="hero-title-en"
              />
            </div>
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Title (Spanish) *</Label>
              <Input
                value={content.hero_title_es || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_title_es: e.target.value }))}
                placeholder="Haz Tu Evento Inolvidable"
                className="rounded-xl text-sm"
                disabled={!isManager}
                data-testid="hero-title-es"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Description (English) *</Label>
              <Textarea
                value={content.hero_description_en || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_description_en: e.target.value }))}
                placeholder="Premium chairs, tables, and bouncy castles for weddings, parties, and corporate events."
                className="rounded-xl text-sm"
                rows={3}
                disabled={!isManager}
                data-testid="hero-description-en"
              />
            </div>
            <div>
              <Label className="text-charcoal/70 mb-2 block text-sm">Description (Spanish) *</Label>
              <Textarea
                value={content.hero_description_es || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_description_es: e.target.value }))}
                placeholder="Sillas, mesas y castillos inflables premium para bodas, fiestas y eventos corporativos."
                className="rounded-xl text-sm"
                rows={3}
                disabled={!isManager}
                data-testid="hero-description-es"
              />
            </div>
          </div>
          {isManager && (
            <Button
              onClick={saveHeroText}
              disabled={savingHomepage}
              className="bg-sage hover:bg-sage/90 text-white"
            >
              {savingHomepage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Hero Text
            </Button>
          )}
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
        <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal mb-4">Hero Image</h3>
        <p className="text-xs sm:text-sm text-charcoal/60 mb-4">The main image shown at the top of the homepage.</p>
        
        <div className="space-y-4">
          <div>
            <Label className="text-charcoal/70 mb-2 block text-sm">Image URL</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="url"
                value={content.hero_image_url}
                onChange={(e) => setContent(prev => ({ ...prev, hero_image_url: e.target.value }))}
                placeholder="https://..."
                className="rounded-xl flex-1 text-sm"
                disabled={!isManager}
                data-testid="hero-image-url"
              />
              {isManager && (
                <Button
                  onClick={saveHeroImage}
                  disabled={savingHomepage}
                  className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto"
                >
                  {savingHomepage ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                </Button>
              )}
            </div>
          </div>
          {content.hero_image_url && (
            <div className="rounded-xl overflow-hidden border border-charcoal/10 max-w-md">
              <img 
                src={content.hero_image_url} 
                alt="Hero preview" 
                className="w-full h-48 object-cover"
                onError={(e) => e.target.style.display='none'}
              />
            </div>
          )}
        </div>
      </div>


      {/* Delivery Fee Section */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
        <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal mb-4">Delivery & Pickup Fee</h3>
        <p className="text-xs sm:text-sm text-charcoal/60 mb-4">This fee is applied to bookings and reflected in invoices/emails.</p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
          <div className="w-full sm:max-w-xs">
            <Label className="text-charcoal/70 mb-2 block text-sm">Fee (USD)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={content.delivery_pickup_fee ?? 0}
              onChange={(e) => setContent(prev => ({ ...prev, delivery_pickup_fee: e.target.value }))}
              className="rounded-xl"
              disabled={!isManager}
              data-testid="delivery-fee-input"
            />
          </div>
          {isManager && (
            <Button onClick={saveDeliveryFee} disabled={savingHomepage} className="bg-sage hover:bg-sage/90 text-white w-full sm:w-auto">
              {savingHomepage ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Fee"}
            </Button>
          )}
        </div>
      </div>

      {/* Equipment Categories Section */}
      <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="font-heading font-bold text-base sm:text-lg text-charcoal">Equipment Categories</h3>
            <p className="text-xs sm:text-sm text-charcoal/60 mt-1">Categories shown in the "Our Equipment" section.</p>
          </div>
          {isManager && (
            <Button
              onClick={onAddCategory}
              className="bg-sage hover:bg-sage/90 text-white rounded-full w-full sm:w-auto"
              data-testid="add-category-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Category</span>
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {content.equipment_categories.length === 0 ? (
            <p className="text-center text-charcoal/60 py-8">No categories configured</p>
          ) : content.equipment_categories.map((cat, index) => (
            <div key={cat.id} className="border border-charcoal/10 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden border border-charcoal/10 flex-shrink-0">
                  <img 
                    src={cat.image_url} 
                    alt={cat.title_en} 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src='https://via.placeholder.com/96?text=No+Image'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-charcoal">{cat.title_en}</p>
                      <p className="text-sm text-charcoal/60">{cat.title_es}</p>
                    </div>
                    <span className="bg-charcoal/10 text-charcoal text-xs px-2 py-1 rounded-full flex-shrink-0">
                      {cat.key}
                    </span>
                  </div>
                  <p className="text-sm text-charcoal/70 mt-2 line-clamp-2">{cat.description_en}</p>
                  {isManager && (
                    <div className="flex items-center gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onEditCategory(cat)}
                        data-testid={`edit-category-${cat.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        onClick={() => onDeleteCategory(cat.id)}
                        data-testid={`delete-category-${cat.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Analytics Panel Component
const AnalyticsPanel = ({ 
  analytics, loading, range, setRange, 
  startDate, endDate, setStartDate, setEndDate, 
  onApplyCustomRange 
}) => {
  const rangeOptions = [
    { value: "week", label: "Last 7 Days" },
    { value: "month", label: "Last 30 Days" },
    { value: "3months", label: "Last 3 Months" },
    { value: "6months", label: "Last 6 Months" },
    { value: "year", label: "Last Year" },
    { value: "all", label: "All Time" },
    { value: "custom", label: "Custom Range" },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Date Range Selector */}
      <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-indigo-100 flex-shrink-0">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Analytics</h2>
              <p className="text-xs sm:text-sm text-charcoal/60">Performance insights based on approved bookings</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-full sm:w-44" data-testid="analytics-range-select">
                <CalendarDays className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                {rangeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Custom Date Range */}
        {range === "custom" && (
          <div className="bg-cream/50 rounded-xl p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Label className="text-charcoal/70 mb-2 block text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-xl"
                  data-testid="analytics-start-date"
                />
              </div>
              <div className="flex-1">
                <Label className="text-charcoal/70 mb-2 block text-sm">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-xl"
                  data-testid="analytics-end-date"
                />
              </div>
              <Button 
                onClick={onApplyCustomRange}
                disabled={!startDate || !endDate}
                className="bg-sage hover:bg-sage/90 text-white"
                data-testid="apply-date-range-btn"
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sage" />
          </div>
        ) : analytics ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-emerald-700 font-medium">Total Revenue</span>
                </div>
                <p className="font-heading font-bold text-3xl text-emerald-800">
                  ${analytics.summary?.total_revenue?.toLocaleString() || '0'}
                </p>
                <p className="text-sm text-emerald-600 mt-1">
                  Avg: ${analytics.summary?.avg_booking_value?.toFixed(2) || '0'} per booking
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-blue-700 font-medium">Total Bookings</span>
                </div>
                <p className="font-heading font-bold text-3xl text-blue-800">
                  {analytics.summary?.total_bookings || 0}
                </p>
                <p className="text-sm text-blue-600 mt-1">Approved bookings only</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-purple-700 font-medium">Top Product</span>
                </div>
                <p className="font-heading font-bold text-xl text-purple-800 truncate">
                  {analytics.top_products?.[0]?.name || 'N/A'}
                </p>
                <p className="text-sm text-purple-600 mt-1">
                  {analytics.top_products?.[0]?.quantity || 0} units booked
                </p>
              </div>
            </div>

            {/* Top Products */}
            <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6 mb-6">
              <h3 className="font-heading font-bold text-lg text-charcoal mb-4">Most Booked Products</h3>
              {analytics.top_products?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.top_products.map((product, index) => (
                    <div key={product.name} className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-700' : 'bg-charcoal/30'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-charcoal truncate">{product.name}</p>
                        <p className="text-xs text-charcoal/60">
                          {product.bookings} booking{product.bookings !== 1 ? 's' : ''} • ${product.revenue?.toFixed(2)} revenue
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sage">{product.quantity}</p>
                        <p className="text-xs text-charcoal/50">units</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-charcoal/60 py-8">No booking data for this period</p>
              )}
            </div>

            {/* Revenue by Category */}
            <div className="border border-charcoal/10 rounded-2xl p-4 sm:p-6">
              <h3 className="font-heading font-bold text-lg text-charcoal mb-4">Revenue by Category</h3>
              {analytics.category_revenue?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.category_revenue.map((cat, index) => {
                    const maxRevenue = Math.max(...analytics.category_revenue.map(c => c.revenue));
                    const percentage = maxRevenue > 0 ? (cat.revenue / maxRevenue) * 100 : 0;
                    const colors = ['bg-sage', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500'];
                    
                    return (
                      <div key={cat.category} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-charcoal">{cat.category}</span>
                          <span className="font-bold text-charcoal">${cat.revenue.toLocaleString()}</span>
                        </div>
                        <div className="h-3 bg-charcoal/10 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colors[index % colors.length]} rounded-full transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-charcoal/60 py-8">No revenue data for this period</p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-20 text-charcoal/60">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Select a date range to view analytics</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BookingsTable = ({ bookings, statusFilter, setStatusFilter, onView, onApprove, onReject, onExport, exporting, onCleanup, isManager, onDownloadInvoice }) => (
  <div className="bg-white rounded-3xl shadow-sm">
    <div className="p-4 sm:p-6 border-b border-charcoal/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Bookings</h2>
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
        {isManager && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={onCleanup}
            className="border-red-200 text-red-600 hover:bg-red-50"
            data-testid="cleanup-btn"
          >
            <Trash2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Cleanup</span>
          </Button>
        )}
        <Button 
          variant="outline" 
          size="sm"
          onClick={onExport}
          disabled={exporting}
          className="border-sage text-sage hover:bg-sage/10"
          data-testid="export-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Download className="w-4 h-4 sm:mr-2" />}
          <span className="hidden sm:inline">Export</span>
        </Button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-28 sm:w-40" data-testid="status-filter"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    
    {/* Mobile Card View */}
    <div className="lg:hidden p-4 space-y-3">
      {bookings.length === 0 ? (
        <p className="text-center text-charcoal/60 py-8">No bookings</p>
      ) : bookings.map(b => (
        <div key={b.id} data-testid={`booking-card-${b.id}`} className="border border-charcoal/10 rounded-xl p-4 space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <span className="font-mono font-bold text-sage text-sm">{b.booking_id || b.id.slice(0,8).toUpperCase()}</span>
              <p className="font-medium text-charcoal mt-1">{b.customer_name}</p>
              <p className="text-xs text-charcoal/60">{b.customer_email}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.status === "pending" ? "status-pending" : b.status === "approved" ? "status-approved" : "status-rejected"}`}>{b.status}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-charcoal/70">{b.start_date}</span>
            <span className="font-medium text-charcoal">${b.total_price.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-charcoal/10">
            <Button variant="outline" size="sm" onClick={() => onView(b)} className="flex-1"><Eye className="w-4 h-4 mr-1" />View</Button>
            <Button variant="outline" size="sm" onClick={() => onDownloadInvoice(b.id, b.booking_id || b.id.slice(0,8).toUpperCase())} className="text-blue-600"><FileText className="w-4 h-4" /></Button>
            {b.status === "pending" && isManager && <>
              <Button variant="outline" size="sm" className="text-green-600" onClick={() => onApprove(b.id)}><Check className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" className="text-red-500" onClick={() => onReject(b.id)}><X className="w-4 h-4" /></Button>
            </>}
          </div>
        </div>
      ))}
    </div>
    
    {/* Desktop Table View */}
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full">
        <thead><tr className="border-b border-charcoal/10"><th className="text-left p-4 font-medium text-charcoal/60">Booking ID</th><th className="text-left p-4 font-medium text-charcoal/60">Customer</th><th className="text-left p-4 font-medium text-charcoal/60">Dates</th><th className="text-left p-4 font-medium text-charcoal/60">Items</th><th className="text-left p-4 font-medium text-charcoal/60">Total</th><th className="text-left p-4 font-medium text-charcoal/60">Status</th><th className="text-left p-4 font-medium text-charcoal/60">Actions</th></tr></thead>
        <tbody>
          {bookings.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-charcoal/60">No bookings</td></tr> : bookings.map(b => (
            <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-charcoal/5 hover:bg-cream">
              <td className="p-4"><span className="font-mono font-bold text-sage">{b.booking_id || b.id.slice(0,8).toUpperCase()}</span></td>
              <td className="p-4"><p className="font-medium text-charcoal">{b.customer_name}</p><p className="text-sm text-charcoal/60">{b.customer_email}</p></td>
              <td className="p-4 text-charcoal/70">{b.start_date} - {b.end_date}</td>
              <td className="p-4 text-charcoal/70">{b.items.length} items</td>
              <td className="p-4 font-medium text-charcoal">${b.total_price.toFixed(2)}</td>
              <td className="p-4"><span className={`px-3 py-1 rounded-full text-sm font-medium ${b.status === "pending" ? "status-pending" : b.status === "approved" ? "status-approved" : "status-rejected"}`}>{b.status}</span></td>
              <td className="p-4"><div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" data-testid={`view-booking-${b.id}`} onClick={() => onView(b)} title="View Details"><Eye className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" data-testid={`invoice-booking-${b.id}`} onClick={() => onDownloadInvoice(b.id, b.booking_id || b.id.slice(0,8).toUpperCase())} className="text-blue-600" title="Download Invoice"><FileText className="w-4 h-4" /></Button>
                {b.status === "pending" && isManager && <>
                  <Button variant="ghost" size="icon" data-testid={`approve-booking-${b.id}`} className="text-green-600" onClick={() => onApprove(b.id)} title="Approve"><Check className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`reject-booking-${b.id}`} className="text-red-500" onClick={() => onReject(b.id)} title="Reject"><X className="w-4 h-4" /></Button>
                </>}
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ProductsTable = ({ products, onAdd, onEdit, onDelete, onMoveProduct, isManager }) => (
  <div className="bg-white rounded-3xl shadow-sm">
    <div className="p-4 sm:p-6 border-b border-charcoal/10 flex justify-between items-center">
      <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Products</h2>
      {isManager && (
        <Button data-testid="add-product-btn" onClick={onAdd} className="bg-sage hover:bg-sage/90 text-white rounded-full">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Product</span>
        </Button>
      )}
    </div>
    
    {/* Mobile Card View */}
    <div className="lg:hidden p-4 space-y-3">
      {products.length === 0 ? (
        <p className="text-center text-charcoal/60 py-8">No products</p>
      ) : products.map(p => (
        <div key={p.id} data-testid={`product-card-${p.id}`} className="border border-charcoal/10 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <img src={p.image_url} alt={p.name} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-charcoal truncate">{p.name}</p>
              <p className="text-sm text-charcoal/60 capitalize">{p.category.replace("_", " ")}</p>
              <div className="flex items-center gap-4 mt-2">
                <span className="font-bold text-sage">${p.price_per_day}/day</span>
                <span className="text-sm text-charcoal/60">Stock: {p.total_inventory}</span>
              </div>
            </div>
          </div>
          {isManager && (
            <div className="flex items-center gap-2 pt-2 border-t border-charcoal/10">
              <Button variant="outline" size="sm" onClick={() => onMoveProduct(p.id, -1)} data-testid={`move-up-product-mobile-${p.id}`}>
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onMoveProduct(p.id, 1)} data-testid={`move-down-product-mobile-${p.id}`}>
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => onEdit(p)} className="flex-1" data-testid={`edit-product-mobile-${p.id}`}>
                <Edit className="w-4 h-4 mr-2" />Edit
              </Button>
              <Button variant="outline" size="sm" className="text-red-500 border-red-200" onClick={() => onDelete(p.id)} data-testid={`delete-product-mobile-${p.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
    
    {/* Desktop Table View */}
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full">
        <thead><tr className="border-b border-charcoal/10"><th className="text-left p-4 font-medium text-charcoal/60">Product</th><th className="text-left p-4 font-medium text-charcoal/60">Category</th><th className="text-left p-4 font-medium text-charcoal/60">Price/Day</th><th className="text-left p-4 font-medium text-charcoal/60">Inventory</th>{isManager && <th className="text-left p-4 font-medium text-charcoal/60">Actions</th>}</tr></thead>
        <tbody>
          {products.length === 0 ? <tr><td colSpan={isManager ? 5 : 4} className="p-8 text-center text-charcoal/60">No products</td></tr> : products.map(p => (
            <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-charcoal/5 hover:bg-cream">
              <td className="p-4"><div className="flex items-center gap-3"><img src={p.image_url} alt={p.name} className="w-12 h-12 object-cover rounded-lg" /><p className="font-medium text-charcoal">{p.name}</p></div></td>
              <td className="p-4 text-charcoal/70 capitalize">{p.category.replace("_", " ")}</td>
              <td className="p-4 font-medium text-charcoal">${p.price_per_day}</td>
              <td className="p-4 text-charcoal/70">{p.total_inventory}</td>
              {isManager && (
                <td className="p-4"><div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" data-testid={`move-up-product-${p.id}`} onClick={() => onMoveProduct(p.id, -1)}><ArrowUp className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`move-down-product-${p.id}`} onClick={() => onMoveProduct(p.id, 1)}><ArrowDown className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`edit-product-${p.id}`} onClick={() => onEdit(p)}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`delete-product-${p.id}`} className="text-red-500" onClick={() => onDelete(p.id)}><Trash2 className="w-4 h-4" /></Button>
                </div></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ProductDialog = ({ open, onClose, form, setForm, onSubmit, isEdit, categoryOptions }) => {
  const addImageUrl = () => {
    setForm(f => ({ ...f, image_urls: [...f.image_urls, ""] }));
  };
  
  const updateImageUrl = (index, value) => {
    setForm(f => {
      const newUrls = [...f.image_urls];
      newUrls[index] = value;
      return { ...f, image_urls: newUrls };
    });
  };
  
  const removeImageUrl = (index) => {
    setForm(f => ({
      ...f,
      image_urls: f.image_urls.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Product Name (English) *</Label>
            <Input data-testid="product-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g., Elegant White Chairs" />
          </div>
          <div>
            <Label>Product Name (Spanish)</Label>
            <Input data-testid="product-name-es-input" value={form.name_es} onChange={e => setForm(f => ({ ...f, name_es: e.target.value }))} placeholder="e.g., Sillas Blancas Elegantes" />
            <p className="text-xs text-charcoal/50 mt-1">Leave empty to use English name as fallback</p>
          </div>
          <div>
            <Label>Description (English) *</Label>
            <Textarea data-testid="product-description-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} required />
          </div>
          <div>
            <Label>Description (Spanish)</Label>
            <Textarea data-testid="product-description-es-input" value={form.description_es} onChange={e => setForm(f => ({ ...f, description_es: e.target.value }))} rows={2} placeholder="Descripción en español..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Input
                data-testid="product-category-input"
                list="product-category-options"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                required
                placeholder="e.g., chairs"
              />
              <datalist id="product-category-options">
                {categoryOptions.map(category => (
                  <option key={category} value={category} />
                ))}
              </datalist>
              <p className="text-xs text-charcoal/50 mt-1">Choose an existing category or type a new one.</p>
            </div>
            <div><Label>Price/Day ($)</Label><Input type="number" step="0.01" min="0" data-testid="product-price-input" value={form.price_per_day} onChange={e => setForm(f => ({ ...f, price_per_day: e.target.value }))} required /></div>
          </div>
          <div><Label>Inventory</Label><Input type="number" min="1" data-testid="product-inventory-input" value={form.total_inventory} onChange={e => setForm(f => ({ ...f, total_inventory: e.target.value }))} required /></div>
          
          {/* Main Image */}
          <div>
            <Label>Main Image URL *</Label>
            <Input type="url" data-testid="product-image-input" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} required placeholder="https://..." />
            {form.image_url && (
              <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden border border-charcoal/10">
                <img src={form.image_url} alt="Main preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
              </div>
            )}
          </div>
          
          {/* Additional Images */}
          <div className="border-t border-charcoal/10 pt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="mb-0">Additional Images</Label>
              <Button type="button" variant="outline" size="sm" onClick={addImageUrl} className="text-sage border-sage hover:bg-sage/10">
                <Plus className="w-4 h-4 mr-1" />Add Image
              </Button>
            </div>
            {form.image_urls && form.image_urls.length > 0 ? (
              <div className="space-y-2">
                {form.image_urls.map((url, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        type="url"
                        value={url}
                        onChange={(e) => updateImageUrl(index, e.target.value)}
                        placeholder="https://..."
                        className="text-sm"
                        data-testid={`product-image-url-${index}`}
                      />
                    </div>
                    {url && (
                      <div className="w-10 h-10 rounded overflow-hidden border border-charcoal/10 flex-shrink-0">
                        <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
                      </div>
                    )}
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeImageUrl(index)} className="text-red-500 hover:bg-red-50 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-charcoal/50">No additional images. Click "Add Image" to add more.</p>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="save-product-btn" className="bg-sage hover:bg-sage/90 text-white">{isEdit ? "Update" : "Create"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const InvoiceAdjustmentDialog = ({ open, onClose, form, setForm, onSubmit, saving }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="font-heading">Adjust Invoice</DialogTitle>
        <DialogDescription>
          Update discount, regenerate invoice, and optionally resend to customer.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label>Discount Amount (USD)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.discount_amount}
            onChange={(e) => setForm(prev => ({ ...prev, discount_amount: e.target.value }))}
            data-testid="invoice-discount-input"
            required
          />
        </div>
        <div>
          <Label>Notes (optional)</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            data-testid="invoice-discount-notes"
          />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-charcoal/10 p-3">
          <span className="text-sm text-charcoal/70">Resend updated invoice to customer</span>
          <Switch
            checked={!!form.resend_email}
            onCheckedChange={(checked) => setForm(prev => ({ ...prev, resend_email: checked }))}
            data-testid="invoice-resend-switch"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" className="bg-sage hover:bg-sage/90 text-white" disabled={saving} data-testid="save-invoice-adjustment-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Adjustment
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

const BookingDialog = ({ open, onClose, booking, products = [], onApprove, onReject, isManager, onDownloadInvoice, onAdjustInvoice, onSaveEdit, savingEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!booking) {
      setForm(null);
      setIsEditing(false);
      return;
    }
    setForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      customer_phone: booking.customer_phone || "",
      delivery_address: booking.delivery_address || "",
      start_date: booking.start_date || "",
      end_date: booking.end_date || "",
      event_start_time: booking.event_start_time || "",
      event_end_time: booking.event_end_time || "",
      delivery_earliest: booking.delivery_earliest || "",
      pickup_latest: booking.pickup_latest || "",
      notes: booking.notes || "",
      resend_email: true,
      items: (booking.items || []).map((item) => ({ ...item }))
    });
    setIsEditing(false);
  }, [booking, open]);

  const updateItem = (idx, updates) => {
    setForm((prev) => {
      if (!prev) return prev;
      const nextItems = [...prev.items];
      nextItems[idx] = { ...nextItems[idx], ...updates };
      return { ...prev, items: nextItems };
    });
  };

  const addItem = () => {
    const firstProduct = products[0];
    if (!firstProduct) return;
    setForm((prev) => ({
      ...prev,
      items: [
        ...(prev?.items || []),
        {
          product_id: firstProduct.id,
          product_name: firstProduct.name,
          quantity: 1,
          price_per_day: Number(firstProduct.price_per_day || 0)
        }
      ]
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, itemIdx) => itemIdx !== idx) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!booking?.id || !form) return;
    if (!form.items?.length) {
      toast.error("Please keep at least one product on the booking");
      return;
    }
    const payload = {
      ...form,
      items: form.items.map((item) => ({
        ...item,
        quantity: Math.max(1, Number(item.quantity || 1)),
        price_per_day: Number(item.price_per_day || 0)
      }))
    };
    const success = await onSaveEdit?.(booking.id, payload);
    if (success) setIsEditing(false);
  };

  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-heading">Booking Details</DialogTitle></DialogHeader>
      {booking && <div className="space-y-4">
        <div className="bg-midnight text-white rounded-xl p-4 text-center">
          <p className="text-xs uppercase tracking-wide text-white/60">Booking Reference</p>
          <p className="font-mono font-bold text-2xl tracking-wider">{booking.booking_id || booking.id.slice(0,8).toUpperCase()}</p>
        </div>
        <div className="flex justify-between items-start"><div><p className="font-heading font-bold text-lg text-charcoal">{booking.customer_name}</p><p className="text-charcoal/60">{booking.customer_email}</p><p className="text-charcoal/60">{booking.customer_phone}</p></div><span className={`px-3 py-1 rounded-full text-sm font-medium ${booking.status === "pending" ? "status-pending" : booking.status === "approved" ? "status-approved" : "status-rejected"}`}>{booking.status}</span></div>
        {isManager && (
          <Button
            variant={isEditing ? "secondary" : "outline"}
            onClick={() => setIsEditing((prev) => !prev)}
            className="w-full"
            data-testid="dialog-toggle-edit-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            {isEditing ? "Cancel Editing" : "Edit Booking Details"}
          </Button>
        )}
        {isEditing && form ? (
          <form onSubmit={handleSave} className="space-y-4 border rounded-xl p-4 bg-sage/5">
            <p className="text-sm font-medium text-charcoal">Edit customer, schedule, address, notes, and items.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm(prev => ({ ...prev, customer_name: e.target.value }))} required /></div>
              <div><Label>Customer Email</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm(prev => ({ ...prev, customer_email: e.target.value }))} required /></div>
              <div><Label>Customer Phone</Label><Input value={form.customer_phone} onChange={(e) => setForm(prev => ({ ...prev, customer_phone: e.target.value }))} required /></div>
              <div><Label>Delivery Address</Label><Input value={form.delivery_address} onChange={(e) => setForm(prev => ({ ...prev, delivery_address: e.target.value }))} required /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))} required /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))} required /></div>
              <div><Label>Event Start Time</Label><Input type="time" value={form.event_start_time} onChange={(e) => setForm(prev => ({ ...prev, event_start_time: e.target.value }))} /></div>
              <div><Label>Event End Time</Label><Input type="time" value={form.event_end_time} onChange={(e) => setForm(prev => ({ ...prev, event_end_time: e.target.value }))} /></div>
              <div><Label>Earliest Delivery</Label><Input type="time" value={form.delivery_earliest} onChange={(e) => setForm(prev => ({ ...prev, delivery_earliest: e.target.value }))} /></div>
              <div><Label>Latest Pickup</Label><Input type="time" value={form.pickup_latest} onChange={(e) => setForm(prev => ({ ...prev, pickup_latest: e.target.value }))} /></div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-charcoal/10 p-3">
              <span className="text-sm text-charcoal/70">Resend updated invoice to customer</span>
              <Switch
                checked={!!form.resend_email}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, resend_email: checked }))}
                data-testid="booking-edit-resend-switch"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} disabled={!products.length}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
              {(form.items || []).map((item, idx) => (
                <div key={`${item.product_id}-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 border rounded-lg p-3">
                  <div className="md:col-span-6">
                    <Label>Product</Label>
                    <Select
                      value={item.product_id}
                      onValueChange={(productId) => {
                        const selected = products.find((product) => product.id === productId);
                        if (!selected) return;
                        updateItem(idx, {
                          product_id: selected.id,
                          product_name: selected.name,
                          price_per_day: Number(selected.price_per_day || 0)
                        });
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>{product.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Qty</Label>
                    <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                  </div>
                  <div className="md:col-span-3">
                    <Label>Price/Day</Label>
                    <Input type="number" min="0" step="0.01" value={item.price_per_day} onChange={(e) => updateItem(idx, { price_per_day: e.target.value })} />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={(form.items || []).length <= 1}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)} disabled={savingEdit}>Cancel</Button>
              <Button type="submit" className="bg-sage hover:bg-sage/90 text-white" disabled={savingEdit} data-testid="save-booking-edit-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Booking Changes
              </Button>
            </div>
          </form>
        ) : (
          <>
        {booking.delivery_address && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-medium text-green-800 mb-2">📍 Delivery Address</p>
            <p className="font-medium text-charcoal mb-2">{booking.delivery_address}</p>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking.delivery_address)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm bg-green-600 text-white px-3 py-1.5 rounded-full hover:bg-green-700 transition-colors">
              Open in Google Maps
            </a>
          </div>
        )}
        <div className="bg-cream rounded-xl p-4"><p className="text-sm text-charcoal/60 mb-1">Rental Dates</p><p className="font-medium text-charcoal">{booking.start_date} to {booking.end_date}</p></div>
        {(booking.event_start_time || booking.event_end_time || booking.delivery_earliest || booking.pickup_latest) && (
          <div className="bg-sage/5 border border-sage/20 rounded-xl p-4">
            <p className="text-sm font-medium text-sage mb-2">Event & Logistics Schedule</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {booking.event_start_time && <div><span className="text-charcoal/60">Event Start:</span> <span className="font-medium text-charcoal">{formatTime12hr(booking.event_start_time)}</span></div>}
              {booking.event_end_time && <div><span className="text-charcoal/60">Event End:</span> <span className="font-medium text-charcoal">{formatTime12hr(booking.event_end_time)}</span></div>}
              {booking.delivery_earliest && <div><span className="text-charcoal/60">Earliest Delivery:</span> <span className="font-medium text-charcoal">{formatTime12hr(booking.delivery_earliest)}</span></div>}
              {booking.pickup_latest && <div><span className="text-charcoal/60">Latest Pickup:</span> <span className="font-medium text-charcoal">{formatTime12hr(booking.pickup_latest)}</span></div>}
            </div>
          </div>
        )}
        <div><p className="text-sm text-charcoal/60 mb-2">Items</p>{booking.items.map((i, idx) => <div key={idx} className="flex justify-between bg-cream rounded-lg p-3 mb-2"><span>{i.product_name}</span><span className="text-charcoal/70">{i.quantity} × ${i.price_per_day}/day</span></div>)}</div>
        <div className="pt-4 border-t space-y-2">
          {typeof booking.items_subtotal === "number" && (
            <div className="flex justify-between text-sm text-charcoal/70">
              <span>Items Subtotal</span>
              <span>${booking.items_subtotal.toFixed(2)}</span>
            </div>
          )}
          {typeof booking.delivery_fee === "number" && (
            <div className="flex justify-between text-sm text-charcoal/70">
              <span>Delivery & Pickup Fee</span>
              <span>${booking.delivery_fee.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-1">
            <span className="font-bold text-lg">Total</span>
            <span className="font-heading font-bold text-2xl text-sage">${booking.total_price.toFixed(2)}</span>
          </div>
        </div>
        {booking.notes && <div className="bg-sage-light/10 rounded-xl p-4"><p className="text-sm text-charcoal/60 mb-1">Notes</p><p className="text-charcoal">{booking.notes}</p></div>}
        

        {isManager && (
          <Button
            variant="outline"
            onClick={() => onAdjustInvoice(booking)}
            className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="dialog-adjust-invoice-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Adjust Invoice & Resend
          </Button>
        )}

        {/* Download Invoice Button */}
        <Button 
          variant="outline" 
          onClick={() => onDownloadInvoice(booking.id, booking.booking_id || booking.id.slice(0,8).toUpperCase())}
          className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
          data-testid="dialog-download-invoice-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Download Invoice
        </Button>
        
        {booking.status === "pending" && isManager && <div className="flex gap-3 pt-4"><Button data-testid="dialog-approve-btn" onClick={onApprove} className="flex-1 bg-green-600 hover:bg-green-700 text-white"><Check className="w-4 h-4 mr-2" />Approve</Button><Button data-testid="dialog-reject-btn" variant="destructive" onClick={onReject} className="flex-1"><X className="w-4 h-4 mr-2" />Reject</Button></div>}
        {booking.status === "pending" && !isManager && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-amber-800 text-sm">Only managers can approve or reject bookings</p>
          </div>
        )}
          </>
        )}
      </div>}
    </DialogContent>
  </Dialog>
  );
};

const CalendarView = ({ bookings, calendarDate, setCalendarDate, onBookingClick }) => {
  const [popup, setPopup] = useState(null);
  
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const shortMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  const prevMonth = () => setCalendarDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCalendarDate(new Date(year, month + 1, 1));
  
  // Filter bookings that are not rejected
  const activeBookings = bookings.filter(b => b.status !== "rejected");
  
  // Get bookings for a specific day
  const getBookingsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return activeBookings.filter(b => {
      const start = b.start_date;
      const end = b.end_date;
      return dateStr >= start && dateStr <= end;
    });
  };
  
  const days = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  
  return (
    <div className="bg-white rounded-3xl shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
        <h2 className="font-heading font-bold text-lg sm:text-xl text-charcoal">Calendar</h2>
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month" className="h-8 w-8 sm:h-10 sm:w-10"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
          <span className="font-heading font-bold text-base sm:text-lg w-28 sm:w-40 text-center">
            <span className="sm:hidden">{shortMonthNames[month]} {year}</span>
            <span className="hidden sm:inline">{monthNames[month]} {year}</span>
          </span>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month" className="h-8 w-8 sm:h-10 sm:w-10"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></Button>
        </div>
      </div>
      
      <div className="flex gap-3 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm">
        <div className="flex items-center gap-1 sm:gap-2"><div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-amber-400"></div><span>Pending</span></div>
        <div className="flex items-center gap-1 sm:gap-2"><div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-green-500"></div><span>Approved</span></div>
      </div>
      
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="p-1 sm:p-2 text-center font-medium text-charcoal/60 text-xs sm:text-sm">
            <span className="sm:hidden">{d}</span>
            <span className="hidden sm:inline">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][i]}</span>
          </div>
        ))}
        {days.map((day, idx) => {
          const dayBookings = day ? getBookingsForDay(day) : [];
          const hasBookings = dayBookings.length > 0;
          return (
            <div key={idx} className={`min-h-12 sm:min-h-24 p-0.5 sm:p-1 border border-charcoal/10 rounded sm:rounded-lg ${day ? 'bg-cream/50' : 'bg-transparent'} relative`}>
              {day && (
                <>
                  <span className="text-xs sm:text-sm text-charcoal/70">{day}</span>
                  {/* Mobile: Show dots only */}
                  <div className="sm:hidden flex justify-center gap-0.5 mt-0.5">
                    {dayBookings.slice(0, 3).map((b, i) => (
                      <button
                        key={b.id + i}
                        onClick={() => setPopup(popup?.id === b.id ? null : b)}
                        className={`w-2 h-2 rounded-full ${b.status === "pending" ? "bg-amber-400" : "bg-green-500"}`}
                        data-testid={`cal-booking-mobile-${b.id}`}
                      />
                    ))}
                    {dayBookings.length > 3 && <span className="text-[8px] text-charcoal/50">+{dayBookings.length - 3}</span>}
                  </div>
                  {/* Desktop: Show booking names */}
                  <div className="hidden sm:block mt-1 space-y-1">
                    {dayBookings.slice(0, 3).map((b, i) => (
                      <button
                        key={b.id + i}
                        onClick={() => setPopup(popup?.id === b.id ? null : b)}
                        className={`w-full text-left text-xs px-1.5 py-1 rounded truncate cursor-pointer hover:opacity-80 transition-opacity ${b.status === "pending" ? "bg-amber-400 text-amber-900" : "bg-green-500 text-white"}`}
                        data-testid={`cal-booking-${b.id}`}
                      >
                        {b.booking_id || b.customer_name}
                      </button>
                    ))}
                    {dayBookings.length > 3 && <span className="text-xs text-charcoal/50">+{dayBookings.length - 3} more</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Popup */}
      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPopup(null)}>
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative bg-white rounded-2xl shadow-xl p-4 sm:p-5 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPopup(null)} className="absolute top-3 right-3 text-charcoal/50 hover:text-charcoal"><X className="w-5 h-5" /></button>
            <div className="mb-3">
              <span className={`px-2 py-1 rounded text-xs font-medium ${popup.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>{popup.status}</span>
            </div>
            <p className="font-mono font-bold text-sage mb-1 text-sm sm:text-base">{popup.booking_id || popup.id.slice(0,8).toUpperCase()}</p>
            <p className="font-heading font-bold text-base sm:text-lg text-charcoal">{popup.customer_name}</p>
            <p className="text-xs sm:text-sm text-charcoal/60 mb-2">{popup.customer_email}</p>
            <p className="text-xs sm:text-sm text-charcoal/70 mb-3">{popup.start_date} to {popup.end_date}</p>
            <p className="text-xs sm:text-sm text-charcoal/70 mb-4 line-clamp-2">{popup.items.map(i => `${i.product_name} x${i.quantity}`).join(", ")}</p>
            <Button onClick={() => { setPopup(null); onBookingClick(popup); }} className="w-full bg-sage hover:bg-sage/90 text-white">View Full Details</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
